import { HiUserPlus } from 'react-icons/hi2'

type CompletedProps = {
  kind: 'created' | 'updated'
  onNewVisitor: () => void
  onBackToHomepage: () => void
}

// Shown after a successful submit (new lead) or update (existing lead).
// Single visual treatment for both — only the headline differs slightly.
export function RegistrationComplete({ kind, onNewVisitor, onBackToHomepage }: CompletedProps) {
  const heading = kind === 'updated' ? 'Interest Updated' : 'Registration Completed'
  const sub =
    kind === 'updated'
      ? "We've refreshed your details — our team will follow up shortly."
      : 'Thanks — our team will follow up shortly.'

  return (
    <div className="min-h-screen w-full bg-[#f9fafb] text-[#0c0a08]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-[800px] flex-col items-center justify-center gap-20 px-12">
        <div className="flex w-full max-w-[535px] flex-col items-center gap-5 text-center">
          <h1 className="font-display text-5xl leading-[1.15] text-[#004f51]">{heading}</h1>
          <p className="font-body text-lg text-[#111d1e]">{sub}</p>
        </div>
        <div className="flex w-full max-w-[535px] flex-col items-center gap-5">
          <button
            type="button"
            onClick={onNewVisitor}
            className="flex items-center gap-2 rounded-md bg-[#111d1e] px-8 py-4 font-display text-xl font-semibold text-white transition hover:bg-black"
          >
            New Visitor
            <HiUserPlus className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={onBackToHomepage}
            className="rounded-md border border-[#0c0a08]/40 bg-white px-8 py-4 font-display text-xl font-semibold text-[#111d1e]"
          >
            Back to Homepage
          </button>
        </div>
      </div>
    </div>
  )
}

type DuplicateProps = {
  onCancel: () => void
  onConfirm: () => void
  isSubmitting: boolean
}

// Shown when the submitted email already exists on this event. The user
// is offered the explicit choice to update vs cancel — no upserts happen
// without their consent.
export function DuplicateDetected({ onCancel, onConfirm, isSubmitting }: DuplicateProps) {
  return (
    <div className="min-h-screen w-full bg-[#f9fafb] text-[#0c0a08]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-[800px] items-center justify-center px-12 py-20">
        <div className="flex w-full flex-col items-center gap-20 rounded-xl bg-white p-24 text-center shadow-[0px_10px_20px_0px_rgba(189,205,199,0.5)]">
          <div className="flex max-w-[535px] flex-col gap-5">
            <h1 className="font-display text-5xl leading-[1.15] text-[#004f51]">We&rsquo;ve spoken before.</h1>
            <p className="font-body text-lg text-[#111d1e]">
              It looks like you&rsquo;ve already registered with this email. Would you like to update your interest preferences instead?
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-[#0c0a08]/40 bg-white px-8 py-4 font-display text-xl font-semibold text-[#111d1e] disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex items-center gap-2 rounded-md bg-[#111d1e] px-8 py-4 font-display text-xl font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-black"
            >
              {isSubmitting ? 'Updating…' : 'Update Interest'}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
